import useTranslate from "@hooks/use_translate"
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from "@mui/material"
import {
  MigrationState,
  RISK_COLOR,
  isSeedSpec,
  pickText
} from "./migration.models"

type Props = {
  state: MigrationState | null
  starting: boolean
  onCancel: () => void
  onConfirm: (state: MigrationState) => void
}

export default function RunConfirmDialog({
  state,
  starting,
  onCancel,
  onConfirm
}: Props) {
  const { translate, currentLanguage } = useTranslate()
  if (!state) return null
  const { spec } = state

  return (
    <Dialog open onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{translate("migration_confirm_title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            <Typography variant="subtitle1">
              {pickText(spec.title, currentLanguage)}
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={translate(`migration_risk_${spec.risk}`)}
              color={RISK_COLOR[spec.risk]}
            />
          </Stack>
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", fontSize: 12 }}
          >
            {spec.type} · {spec.collection}
          </Typography>
          <Typography variant="body2">
            {isSeedSpec(spec)
              ? translate("migration_documents_line_seed", {
                  present: state.presentCount ?? 0,
                  expected: spec.expectedDocuments.count,
                  missing: state.affectedCount
                })
              : translate("migration_documents_line", {
                  affected: state.affectedCount.toLocaleString(),
                  total: state.totalCount.toLocaleString()
                })}
          </Typography>
          {(spec.notes ?? []).length > 0 && (
            <Alert severity="warning">
              {translate("migration_confirm_notes", {
                count: spec.notes?.length
              })}
            </Alert>
          )}
          <Alert severity="info">{translate("migration_confirm_body")}</Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>
          {translate("migration_confirm_cancel")}
        </Button>
        <Button
          variant="contained"
          color={spec.risk === "DESTRUCTIVE" ? "error" : "primary"}
          disabled={starting}
          onClick={() => onConfirm(state)}
        >
          {translate("migration_confirm_run")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
